import { ComponentProps, FC, useCallback, useMemo } from 'react';

import cn from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { inflect } from 'inflection';
import { FaStar } from 'react-icons/fa';
import {
  useGetPlan,
  useGetPlanSummary,
  useGetProfile,
  useGetSubscription,
} from 'modules/account/settings/hooks';
import Avatar from 'ui/components/img/Avatar';
import { Button } from 'ui/components/common';
import { useAdmin } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import { useRolePermissionContext } from 'utils/providers/RolePermission';
import { useGetAccountUser } from 'pages/private/hooks';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { LogoutButton } from 'pages/Logout';
import { Popover, Transition } from '@headlessui/react';
import { CgSpinner } from 'react-icons/cg';
import { compact } from 'lodash';
import { Can as CanFeatureFlag } from 'utils/providers/FeatureFlag';
import { FEATURE_FLAG_KEYS, STATUS } from 'utils/constants';
import useCopilotContext from 'utils/providers/useCopilotContext';
import { ToadIcon } from 'ui/components/icon';
import { useCan as useCanFeatureFlag } from 'utils/providers/FeatureFlag';

import GlobalHeader from './GlobalHeader';
import { Notification } from './Notification';
import GlobalCreate from './GlobalCreate';
import Help from './Help';

const SubscriptionExpiredBanner = () => {
  const navigate = useNavigate();
  const { data: plan, isLoading: planIsLoading } = useGetPlan();

  const planType = useMemo(() => {
    if (planIsLoading) return;

    if (plan?.status === 'TRIAL') return 'Free Trial';

    return 'Plan';
  }, [plan, planIsLoading]);

  return (
    <div
      className={cn(
        'flex items-center justify-between',
        'h-[58px] w-full bg-[#DD414E] p-4',
      )}
    >
      <div className="flex items-center gap-x-2">
        <p className="text-sm font-semibold text-fill-tertiary">
          Your {planType} Has Ended
        </p>
        <div className="w-px h-3 bg-fill-tertiary" />
        <p className="text-fill-tertiary">
          All your existing data is now in read-only mode.
        </p>
      </div>
      <div className="flex items-center gap-x-4">
        <Button
          size={'sm'}
          intent={'tertiary'}
          className="font-semibold text-fill-tertiary border-fill-tertiary"
          label="SUBSCRIBE NOW"
          onClick={() => navigate('/settings/billing')}
        />
      </div>
    </div>
  );
};

type POPUP_PROFILE_PROPS = {
  isLoading?: boolean;
};

export const PopUpProfile: FC<POPUP_PROFILE_PROPS> = ({ isLoading }) => {
  const {
    state: { isPreview },
  } = useAppWrapperContext();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { data: _profile } = useGetProfile();
  const { data: accountUser } = useGetAccountUser({ enabled: !!isPreview });

  const profile: any = useMemo(() => {
    if (isPreview) {
      return accountUser ?? profile; // TODO: Confirm why it's returning itself
    }

    return _profile;
  }, [_profile, accountUser, isPreview]);

  return (
    <Popover className={cn('relative')}>
      {({ open }) => (
        <>
          <Popover.Button
            className="inline-flex items-center focus:outline-none"
            tabIndex={0}
          >
            <Avatar
              user={profile}
              className="md mr-[12px]"
              isLoading={isLoading}
            />
            <div className="text-[14px] leading-[20px]">
              {profile?.firstName}
            </div>
          </Popover.Button>
          <Transition
            as={Popover.Panel}
            className={cn(
              'absolute',
              'z-50',
              'top-full',
              'bg-fill-tertiary',
              'rounded-[6px]',
              'shadow-leapify-md',
              'p-4',
              'bg-white',
              'p-[22px]',
              'w-[260px]',
              'right-0',
              'text-center',
              'flex flex-col items-center',
            )}
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Avatar
              user={profile}
              className="lg mb-[14px] cursor-default"
              isLoading={isLoading}
            />
            <h3 className="text-[18px] leading-[20px]">{`${profile?.fullName}`}</h3>
            <h4
              className={cn([
                !isAdmin && 'mb-[14px]',
                'text-[14px] font-normal leading-[20px]',
              ])}
            >
              {profile?.emailAddress}
            </h4>
            {!isAdmin && (
              <Button
                onClick={() => navigate('settings/general')}
                className="primary sm"
                label="MANAGE MY ACCOUNT"
              />
            )}
            {isPreview ? null : (
              <div
                className={cn([
                  'mt-[22px]',
                  'border-t-[0.5px]',
                  'w-full',
                  'flex',
                  'flex-col',
                  'items-center',
                  'border-t-[#E4EDF0]',
                  'pt-[22px]',
                ])}
              >
                <LogoutButton />
              </div>
            )}
          </Transition>
        </>
      )}
    </Popover>
  );
};

const SubscriptionPlanBadge = () => {
  const { data: planSummary, isLoading: isPlanSummaryLoading } =
    useGetPlanSummary();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetSubscription();

  const label = useMemo(() => {
    if (planSummary?.status === 'PAID') return 'SUBSCRIBED';

    if (subscription?.isExpired) return 'SUBUSCRIPTION EXPIRED';

    return `${subscription?.daysLeft} ${inflect('day', subscription?.daysLeft)} FREE TRIAL`;
  }, [planSummary?.status, subscription?.isExpired, subscription?.daysLeft]);

  if (isPlanSummaryLoading || isSubscriptionLoading) return null;

  return (
    <Button
      onClick={() => null}
      intent={'tertiary'}
      className="sm font-semibold uppercase"
      label={label}
      disabled
    />
  );
};

const SubscriptionBadge = () => {
  const navigate = useNavigate();
  const {
    state: { rolePermissions },
  } = useRolePermissionContext();
  const { data: subscription } = useGetSubscription();
  const { data: planSummary, isFetching } = useGetPlanSummary();

  const disableSubscriptionButton = useMemo(
    () => rolePermissions?.['plan-plans']?.status !== 'ACTIVE',
    [rolePermissions],
  );

  const label = useMemo(() => {
    if (planSummary?.status === 'PAID') return 'PREMIUM';

    if (subscription?.isExpired) return 'SUBSCRIBE NOW';

    return `${subscription?.daysLeft} ${inflect('day', subscription?.daysLeft)} FREE TRIAL`;
  }, [subscription, planSummary]);

  const icon = useCallback(() => {
    if (planSummary?.status === 'PAID')
      return <FaStar className="w-3 h-3 text-warning-base" />;

    return null;
  }, [planSummary?.status]);

  const isSubscribed = useMemo(
    () => planSummary?.status === 'PAID',
    [planSummary?.status],
  );

  if (isFetching)
    return <CgSpinner className="w-5 h-5 text-primary-base animate-spin" />;

  if (subscription?.isExpired) return;

  if (!isFetching && !planSummary) return <></>;

  return (
    <Button
      onClick={() => navigate('/settings/billing')}
      size="sm"
      intent={subscription?.isExpired ? 'secondary' : 'tertiary'}
      disabled={disableSubscriptionButton}
      className={cn(
        'uppercase',
        isSubscribed &&
          'outline-warning-base text-warning-base bg-[#FFF7E9] pointer-events-none',
      )}
      label={label}
      icon={icon}
      left
    />
  );
};

const useGetModuleAndRecord = (): {
  recordId: string;
  module: 'leads' | 'deals' | '';
} => {
  const location = useLocation();

  if (location.pathname.includes('leads')) {
    const checkRecordID = location.pathname.split('/')?.[3];
    return {
      module: 'leads',
      recordId: checkRecordID ?? '',
    };
  }

  return {
    module: '',
    recordId: '',
  };
};

const Profile: FC = () => {
  const {
    state: { isPreview },
  } = useAppWrapperContext();

  const { state } = useProvider();
  const { isAdmin } = useAdmin();
  const { isLoading } = useGetProfile();

  const { module } = useGetModuleAndRecord();

  const { toggleCopilotSidebar } = useCopilotContext();

  const toadDisabled = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      !useCanFeatureFlag(FEATURE_FLAG_KEYS.TOAD_MODULE) &&
      ['leads', 'deals'].includes(module),
    [module],
  );

  const leftSideItems: ComponentProps<typeof GlobalHeader>['leftSideItems'] = [
    <GlobalCreate />,
  ];

  const rightSideItems: ComponentProps<typeof GlobalHeader>['rightSideItems'] =
    compact([
      toadDisabled && (
        <button onClick={() => toggleCopilotSidebar()}>
          <ToadIcon className="text-fill-tertiary w-[30px] h-[30px]" />
        </button>
      ),
      isPreview && <SubscriptionPlanBadge />,
      !isAdmin && <SubscriptionBadge />,
      <CanFeatureFlag I={STATUS.ACTIVE} this={FEATURE_FLAG_KEYS.HELP_MODULE}>
        <Help />
      </CanFeatureFlag>,
      <Notification />,
      <PopUpProfile isLoading={isLoading} />,
    ]);

  return (
    <div id="profile-wrapper" className={cn(' w-full', '', 'bg-white')}>
      {state?.isOpenBanner && <SubscriptionExpiredBanner />}
      <GlobalHeader
        leftSideItems={leftSideItems}
        rightSideItems={rightSideItems}
      />
    </div>
  );
};

export default Profile;
